export interface Expense {
  id: string;
  title: string;
  /** ISO date string (yyyy-mm-dd), empty string if no date */
  date: string;
  description: string;
  /**
   * Optional external link (e.g. bill payment website URL, or an app deep link
   * such as `hsbc-hk://...` / a bank's smart-app-banner page).
   */
  link: string;
  /** How the goto arrow should treat `link`: a website URL, or an app link. */
  linkType?: 'web' | 'app';
  completed: boolean;
  /**
   * Custom note color as a hex string (e.g. "#ffdfea"); undefined = auto color
   * (stable hash of the id). Stored data from older versions may hold a PALETTE
   * index (number) instead — `colorFor` in data/palette.ts handles both, and the
   * editor normalizes it to hex the next time the note is saved.
   */
  color?: string;
  /** Optional amount (HKD). Shown in the editor only — never on the note card. */
  amount?: number;
  /**
   * Reminder time as "HH:MM" (24h) — when due/overdue pushes fire for this bill.
   * `undefined` = default 08:00. Stored per bill so each can have its own alarm.
   */
  remindAt?: string;
  /**
   * Optional category tag (v0.14) — one of BILL_CATEGORIES; used to group bills
   * with per-category subtotals in the total-expense view. `undefined` = none.
   */
  category?: string;
  /**
   * Days before the due date the "almost due" push fires (v0.14): 1, 3 or 7.
   * `undefined` = default 3. Stored per bill so each can have its own window.
   */
  dueSoonDays?: number;
}

/** Fixed category list — chips in the editor, subtotal groups in the totals view. */
export const BILL_CATEGORIES = [
  'Rent',
  'Utilities',
  'Loan',
  'Credit Card',
  'Insurance',
  'Other',
] as const;

export type BillCategory = (typeof BILL_CATEGORIES)[number];

/** A bill's category, falling back to 'Other' when unset/unknown. */
export function categoryOf(e: Pick<Expense, 'category'> | null | undefined): BillCategory {
  const c = e?.category;
  return c && (BILL_CATEGORIES as readonly string[]).includes(c) ? (c as BillCategory) : 'Other';
}

/** Heads-up windows offered for the "almost due" push. */
export const DUE_SOON_DAYS = [1, 3, 7] as const;
export const DEFAULT_DUE_SOON_DAYS = 3;

/** The bill's "almost due" lead time (days before the due date), defaulting to 3. */
export function dueSoonDaysOf(e: Pick<Expense, 'dueSoonDays'> | null | undefined): number {
  const d = e?.dueSoonDays;
  return d != null && (DUE_SOON_DAYS as readonly number[]).includes(d)
    ? d
    : DEFAULT_DUE_SOON_DAYS;
}

/** The bill's reminder time ("HH:MM"), defaulting to 08:00 when missing/invalid. */
export function remindAtOf(e: Pick<Expense, 'remindAt'> | null | undefined): string {
  return /^\d{2}:\d{2}$/.test(e?.remindAt ?? '') ? e!.remindAt! : '08:00';
}

export function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/** Local-time month key for a date, e.g. `2026-09` */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function createEmptyExpense(): Expense {
  return {
    id: createId(),
    title: '',
    date: '',
    description: '',
    link: '',
    completed: false,
    remindAt: '08:00',
  };
}

export const initialExpenses: Expense[] = [
  {
    id: 'rent-01',
    title: 'Rent',
    date: '2026-08-01',
    description: 'Monthly flat rent — bank transfer to landlord.',
    link: '',
    category: 'Rent',
    completed: false,
  },
  {
    id: 'water-03',
    title: 'Water',
    date: '2026-08-10',
    description: 'WSD water & sewage charge.',
    link: '',
    category: 'Utilities',
    completed: false,
  },
  {
    id: 'elec-02',
    title: 'Electricity',
    date: '2026-08-15',
    description: 'CLP Power bill.',
    link: '',
    category: 'Utilities',
    completed: false,
  },
  {
    id: 'loan-04',
    title: 'University Loan',
    date: '2026-08-20',
    description: 'Monthly student loan repayment.',
    link: '',
    category: 'Loan',
    completed: false,
  },
  {
    id: 'cc-05',
    title: 'HSBC Credit Card',
    date: '2026-08-25',
    description: 'HSBC credit card statement payment.',
    link: 'shortcuts://run-shortcut?name=OpenHSBC',
    linkType: 'app',
    category: 'Credit Card',
    completed: false,
  },
];
